"use client";

import React, { useState, useMemo } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronRight,
  ChevronDown,
  Search,
  HelpCircle,
  Car,
  CreditCard,
  ShieldCheck,
  RefreshCw,
  PhoneCall,
  Mail,
  ArrowRight,
  Sparkles,
  X,
  Clock,
  FileText,
} from "lucide-react";

const SimplePageBackground = dynamic(
  () => import("@/components/SimplePageBackground").then((m) => m.SimplePageBackground),
  { ssr: true }
);

const IKFooter = dynamic(
  () => import("@/components/IKFooter").then((m) => m.IKFooter),
  { ssr: true }
);

interface FAQItemData {
  id: string;
  category: string;
  question: string;
  answer: string;
}

const CATEGORIES = [
  { id: "all", label: "All Questions", icon: HelpCircle },
  { id: "booking", label: "General & Booking", icon: Clock },
  { id: "fleet", label: "Fleet & Vehicles", icon: Car },
  { id: "pricing", label: "Pricing & Payments", icon: CreditCard },
  { id: "cancellation", label: "Cancellation & Refunds", icon: RefreshCw },
  { id: "safety", label: "Safety & Support", icon: ShieldCheck },
];

const FAQ_DATA: FAQItemData[] = [
  // General & Booking
  {
    id: "g1",
    category: "booking",
    question: "How old do I need to be to rent a car with IK Travels?",
    answer:
      "You must be at least 21 years old to rent standard vehicles with IK Travels. For luxury or high-performance premium vehicles, the minimum age requirement is 25 years. All drivers must hold a valid, original driving license for at least 1 year.",
  },
  {
    id: "g2",
    category: "booking",
    question: "What documents do I need to present during pickup?",
    answer:
      "You need to present a valid driving license, a original government-issued photo ID (Aadhaar Card, Passport, or Voter ID), and a credit/debit card or digital payment method for the refundable security deposit. International travelers must present an International Driving Permit (IDP) alongside their native driver's license.",
  },
  {
    id: "g3",
    category: "booking",
    question: "Can someone else drive the rented vehicle?",
    answer:
      "Only drivers registered and verified in the rental agreement are legally permitted to drive the vehicle. You can add co-drivers during the booking process or at pickup by submitting their valid driving license and ID verification.",
  },
  {
    id: "g4",
    category: "booking",
    question: "How far in advance should I book my rental?",
    answer:
      "While we accept instant bookings based on fleet availability, we recommend reserving your vehicle at least 24 to 48 hours in advance—especially during weekends, holidays, and peak travel seasons—to ensure your preferred vehicle model is reserved.",
  },

  // Fleet & Vehicles
  {
    id: "f1",
    category: "fleet",
    question: "What types of vehicles are available in your fleet?",
    answer:
      "IK Travels offers a wide selection of self-drive vehicles including budget hatchbacks, compact sedans, spacious SUVs, luxury sedans, and high-performance motorbikes. All our vehicles are fully sanitized, regularly serviced, and thoroughly inspected before delivery.",
  },
  {
    id: "f2",
    category: "fleet",
    question: "Is there a kilometer limit on rental vehicles?",
    answer:
      "Most of our rental plans come with generous kilometer allowances, and we offer Unlimited Kilometer options on selected car categories. Additional kilometers beyond the included plan rate are charged at a transparent per-km fee outlined during booking.",
  },
  {
    id: "f3",
    category: "fleet",
    question: "What is your fuel policy?",
    answer:
      "We operate on a straightforward 'Full-to-Full' fuel policy. You will receive the vehicle with a full tank of fuel and should return it with a full tank. If returned with less fuel, refuelling charges based on actual fuel costs will apply.",
  },
  {
    id: "f4",
    category: "fleet",
    question: "Are child seats or GPS accessories available?",
    answer:
      "Yes, child safety seats, roof racks, and mobile phone mounts can be requested during the checkout process for a nominal daily fee, subject to availability.",
  },

  // Pricing & Payments
  {
    id: "p1",
    category: "pricing",
    question: "What payment methods are accepted?",
    answer:
      "We accept all major credit cards, debit cards, Net Banking, UPI (Google Pay, PhonePe, Paytm), and digital wallets. All online transactions are encrypted and processed through secure, PCI-DSS compliant payment gateways.",
  },
  {
    id: "p2",
    category: "pricing",
    question: "How does the security deposit work and when is it refunded?",
    answer:
      "A refundable security deposit is held at the start of your trip to cover potential tolls, speed violations, or minor damages. Once the vehicle is returned in good condition and cleared for tolls/challans, the deposit is processed back to your original payment method within 3 to 7 business days.",
  },
  {
    id: "p3",
    category: "pricing",
    question: "Are toll taxes and FASTag included in the rental cost?",
    answer:
      "All our vehicles are equipped with active FASTag devices for seamless highway travel. Toll charges incurred during your journey will be automatically deducted from your FASTag account balance or adjusted from your security deposit upon vehicle return.",
  },
  {
    id: "p4",
    category: "pricing",
    question: "Are there any hidden fees or extra charges?",
    answer:
      "No! IK Travels prides itself on absolute pricing transparency. The price displayed during booking includes base rental, standard vehicle insurance, and applicable taxes. Any optional add-ons or security deposits are clearly itemized before you pay.",
  },

  // Cancellation & Refunds
  {
    id: "c1",
    category: "cancellation",
    question: "What is your cancellation policy?",
    answer:
      "Free cancellations are available up to 24 hours prior to your scheduled pickup time with a 100% full refund. Cancellations made between 6 to 24 hours before pickup incur a small 10% processing fee. Cancellations made within 6 hours of pickup are non-refundable.",
  },
  {
    id: "c2",
    category: "cancellation",
    question: "Can I extend my rental duration after starting the trip?",
    answer:
      "Yes! You can extend your rental reservation directly through our website or by contacting customer support at least 4 hours before your scheduled return time, subject to vehicle availability.",
  },
  {
    id: "c3",
    category: "cancellation",
    question: "What happens if I return the car early?",
    answer:
      "If you return the vehicle earlier than agreed, we will adjust the charges based on actual usage. However, unused hours on short daily rentals may not qualify for partial refunds.",
  },

  // Safety & Support
  {
    id: "s1",
    category: "safety",
    question: "Is roadside assistance included with my rental?",
    answer:
      "Yes! Every IK Travels booking includes complimentary 24/7 Roadside Assistance. In case of a mechanical breakdown, flat tire, or emergency, simply call our dedicated hotline and our team will dispatch immediate support or a replacement vehicle.",
  },
  {
    id: "s2",
    category: "safety",
    question: "What insurance coverage is provided?",
    answer:
      "All IK Travels rental vehicles come with standard Comprehensive Commercial Insurance covering third-party liability and vehicle damages. Premium excess-reduction packages can also be opted during checkout for added peace of mind.",
  },
  {
    id: "s3",
    category: "safety",
    question: "Can I drive the vehicle across state lines?",
    answer:
      "Yes, all our vehicles have nationwide commercial permits. You can drive freely across state borders within India. Please ensure all state entry taxes or toll fees are paid at official border checkposts or FASTag lanes.",
  },
];

function FAQAccordionItem({
  item,
  isOpen,
  onToggle,
}: {
  item: FAQItemData;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-neutral-200/80 dark:border-neutral-800/80 last:border-b-0 transition-colors">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between py-5 px-1 md:px-2 text-left group transition-all"
        aria-expanded={isOpen}
        aria-controls={`faq-ans-${item.id}`}
        id={`faq-ques-${item.id}`}
      >
        <div className="flex items-center gap-3 pr-4">
          <span className="h-2 w-2 rounded-full bg-[#FFB51D] shrink-0 opacity-80 group-hover:scale-125 transition-transform" />
          <h3 className="text-base md:text-lg font-semibold text-[#131222] dark:text-white group-hover:text-[#FFB51D] dark:group-hover:text-[#FFB51D] transition-colors leading-snug">
            {item.question}
          </h3>
        </div>
        <div
          className={`p-1.5 rounded-full border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50 shrink-0 transition-transform duration-300 ${
            isOpen ? "rotate-180 bg-[#FFB51D]/10 border-[#FFB51D]/30" : "group-hover:border-[#FFB51D]/40"
          }`}
        >
          <ChevronDown
            className={`w-4 h-4 text-[#131222] dark:text-white transition-colors ${
              isOpen ? "text-[#FFB51D] dark:text-[#FFB51D]" : ""
            }`}
            aria-hidden
          />
        </div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={`faq-ans-${item.id}`}
            role="region"
            aria-labelledby={`faq-ques-${item.id}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pb-6 pt-1 pl-6 pr-2 md:pr-4 text-neutral-600 dark:text-neutral-400 text-sm md:text-base leading-relaxed border-l-2 border-[#FFB51D]/30 ml-2 mb-2">
              {item.answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQPage() {
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [openId, setOpenId] = useState<string | null>("g1");

  const filteredFAQs = useMemo(() => {
    return FAQ_DATA.filter((item) => {
      const matchesCategory =
        activeCategory === "all" || item.category === activeCategory;
      const matchesSearch =
        searchQuery.trim() === "" ||
        item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  return (
    <SimplePageBackground>
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 flex-1 flex flex-col justify-between">
        <div>
          {/* Breadcrumb Navigation */}
          <nav
            className="flex items-center flex-wrap gap-1.5 md:gap-2 text-xs md:text-sm text-neutral-500 dark:text-neutral-400 mb-8 font-medium"
            aria-label="Breadcrumb"
          >
            <Link
              href="/"
              className="hover:text-[#FFB51D] dark:hover:text-[#FFB51D] transition-colors"
            >
              Home
            </Link>
            <ChevronRight
              className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-600 shrink-0"
              aria-hidden
            />
            <span className="text-[#131222] dark:text-white font-semibold">
              FAQ's
            </span>
          </nav>

          {/* Hero Header */}
          <div className="mb-10 text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FFB51D]/10 dark:bg-[#FFB51D]/20 border border-[#FFB51D]/30 mb-4">
              <Sparkles className="w-4 h-4 text-[#FFB51D]" aria-hidden />
              <span className="text-xs sm:text-sm font-semibold text-[#131222] dark:text-[#FFB51D]">
                Help & Information Center
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#131222] dark:text-white tracking-tight mb-4">
              Frequently Asked <span className="text-[#FFB51D]">Questions</span>
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              Everything you need to know about renting with IK Travels—from booking requirements and vehicle details to payments and roadside safety.
            </p>
            <div className="h-1 w-20 bg-[#FFB51D] mx-auto rounded-full mt-5" />
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-10">
            <div className="relative flex items-center">
              <Search className="absolute left-4 w-5 h-5 text-neutral-400 dark:text-neutral-500 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search questions by keyword (e.g. deposit, fuel, license, insurance)..."
                className="w-full pl-12 pr-10 py-3.5 text-sm md:text-base rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/80 text-[#131222] dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-[#FFB51D]/50 focus:border-[#FFB51D] shadow-sm transition-all"
                aria-label="Search FAQ questions"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3.5 p-1 rounded-full text-neutral-400 hover:text-neutral-600 dark:hover:text-white transition-colors"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center justify-center flex-wrap gap-2 md:gap-3 mb-10">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => {
                    setActiveCategory(cat.id);
                    setOpenId(null);
                  }}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 border ${
                    isActive
                      ? "bg-[#FFB51D] text-[#131222] border-[#FFB51D] shadow-md scale-105"
                      : "bg-white dark:bg-neutral-900/60 border-neutral-200/80 dark:border-neutral-800 text-neutral-600 dark:text-neutral-300 hover:border-[#FFB51D]/50 hover:text-[#131222] dark:hover:text-white"
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{cat.label}</span>
                </button>
              );
            })}
          </div>

          {/* Accordion Container */}
          <div className="max-w-4xl mx-auto mb-16">
            {filteredFAQs.length === 0 ? (
              <div className="rounded-2xl border border-neutral-200/80 dark:border-neutral-800 bg-white dark:bg-neutral-900/40 p-10 text-center shadow-md">
                <HelpCircle className="w-12 h-12 text-[#FFB51D] mx-auto mb-3 opacity-80" />
                <h3 className="text-lg font-bold text-[#131222] dark:text-white mb-2">
                  No matching questions found
                </h3>
                <p className="text-neutral-600 dark:text-neutral-400 text-sm max-w-md mx-auto mb-6">
                  We couldn't find any questions matching "{searchQuery}". Try clearing your search or filtering by category.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setActiveCategory("all");
                  }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#FFB51D] text-[#131222] font-semibold text-sm hover:bg-[#FFB51D]/90 transition-colors shadow"
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              <div className="rounded-2xl bg-white dark:bg-neutral-900/60 border border-neutral-200/80 dark:border-neutral-800 shadow-xl p-4 sm:p-6 md:p-8">
                <div className="divide-y divide-neutral-200/80 dark:divide-neutral-800/80">
                  {filteredFAQs.map((item) => (
                    <FAQAccordionItem
                      key={item.id}
                      item={item}
                      isOpen={openId === item.id}
                      onToggle={() =>
                        setOpenId((prev) => (prev === item.id ? null : item.id))
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* "Still Have Questions?" Contact Support Section */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#131222] via-neutral-900 to-[#1f1d38] p-8 md:p-12 text-white shadow-2xl border border-neutral-800">
              <div className="absolute top-0 right-0 -mt-10 -mr-10 w-48 h-48 rounded-full bg-[#FFB51D]/10 blur-3xl pointer-events-none" />
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                <div className="text-center md:text-left max-w-xl">
                  <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white mb-3">
                    Still have questions or need assistance?
                  </h2>
                  <p className="text-neutral-300 text-sm md:text-base leading-relaxed">
                    Our friendly customer support team is available around the clock to help you with booking inquiries, vehicle choices, or active trip assistance.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row md:flex-col gap-3 w-full sm:w-auto shrink-0">
                  <Link
                    href="/contact"
                    className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-xl bg-[#FFB51D] text-[#131222] font-bold text-sm hover:bg-[#FFC44D] transition-all duration-200 shadow-lg text-center"
                  >
                    <span>Contact Support</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>

                  <a
                    href="tel:+919809548899"
                    className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-xl border border-white/20 bg-white/10 text-white font-medium text-sm hover:bg-white/20 transition-all duration-200 text-center"
                  >
                    <PhoneCall className="w-4 h-4 text-[#FFB51D]" />
                    <span>Call Us Directly</span>
                  </a>
                </div>
              </div>

              {/* Support Info Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8 pt-8 border-t border-white/10">
                <a
                  href="tel:+919809548899"
                  className="flex items-center gap-3 p-3.5 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors group"
                >
                  <div className="p-2.5 rounded-lg bg-[#FFB51D]/20 text-[#FFB51D] group-hover:scale-105 transition-transform">
                    <PhoneCall className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-400 font-medium">Customer Support</p>
                    <p className="text-xs sm:text-sm font-semibold text-white group-hover:text-[#FFB51D] transition-colors">
                      +91 9809548899
                    </p>
                  </div>
                </a>

                <a
                  href="mailto:ikbs8899@gmail.com"
                  className="flex items-center gap-3 p-3.5 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors group"
                >
                  <div className="p-2.5 rounded-lg bg-[#FFB51D]/20 text-[#FFB51D] group-hover:scale-105 transition-transform">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-400 font-medium">Email Inquiries</p>
                    <p className="text-xs sm:text-sm font-semibold text-white group-hover:text-[#FFB51D] transition-colors">
                      ikbs8899@gmail.com
                    </p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Reusable Footer */}
      <IKFooter />
    </SimplePageBackground>
  );
}
